import * as React from 'react';
import { useNavigate } from 'react-router-dom';

// Cle de cache local (partagee par tous les ecrans qui utilisent la VMA :
// Construction, Modeles...), pour eviter un aller-retour serveur a
// chaque changement de page et pour disposer d'une valeur de repli
// immediate pendant que la requete est en cours.
const VMA_STORAGE_KEY = 'lastVma';

export function loadLastVma(): number {
  try {
    const raw = sessionStorage.getItem(VMA_STORAGE_KEY);
    const val = raw ? parseFloat(raw) : NaN;
    return isNaN(val) ? 16.5 : val;
  } catch {
    return 16.5;
  }
}

export function saveLastVma(vma: number): void {
  try {
    sessionStorage.setItem(VMA_STORAGE_KEY, String(vma));
  } catch {
    // sessionStorage indisponible : on ignore silencieusement
  }
}

/**
 * Charge la VMA de reference de l'utilisateur courant depuis la
 * preference enregistree cote serveur (cf UserPreferenceController /
 * AllureController) - l'equivalent, par utilisateur, de la formule
 * "=Allures!C5" utilisee par le classeur Google Sheets d'origine
 * (Code.gs / Generateur.gs) pour tous ses ecrans.
 *
 * Si aucune VMA n'est encore enregistree pour l'utilisateur (jamais
 * saisi de record sur l'ecran Allures), renvoie l'utilisateur y
 * saisir un record plutot que d'afficher un ecran avec une VMA par
 * defaut arbitraire.
 *
 * Avec { exigee: false }, l'ecran reste affiche avec la derniere VMA
 * connue (ex : calendrier, consultable sans VMA).
 *
 * Renvoie un couple [vma, setVma] comme useState : la valeur chargee
 * du serveur pre-remplit le champ, mais reste ajustable ponctuellement
 * dans le formulaire de la page appelante sans redemander au serveur.
 */
export function useVma({ exigee = true }: { exigee?: boolean } = {}): [number, React.Dispatch<React.SetStateAction<number>>] {
  const navigate = useNavigate();
  const [vma, setVma] = React.useState<number>(loadLastVma());

  React.useEffect(() => {
    const token = sessionStorage.getItem('token');
    const request = new Request('/api/preference/vma', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token,
      },
    });

    fetch(request)
      .then((response) => {
        if (response.status === 200) {
          return response.json().then((value: number) => {
            const vmaArrondie = Math.round(value * 10) / 10;
            setVma(vmaArrondie);
            saveLastVma(vmaArrondie);
          });
        }
        if (response.status === 404 && exigee) {
          // Aucune VMA memorisee pour cet utilisateur : direction
          // l'ecran Allures pour qu'il en calcule une.
          navigate('/allures?vmaRequise=1');
          return;
        }
        console.warn('Impossible de récupérer la VMA de l\'utilisateur (statut ' + response.status + ')');
      })
      .catch((error) => {
        console.warn('Erreur lors de la récupération de la VMA de l\'utilisateur', error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, exigee]);

  return [vma, setVma];
}
